// Block model for the visual template builder.
//
// Serialised to Jinja2 on save (see `serialise-blocks.ts`) and stored as the
// opaque `template_blocks` JSON on the backend. The frontend owns the entire
// schema — the backend treats it as a blob.
//
// v1 is intentionally declarative: no loops, no conditionals. The serialiser
// auto-wraps subject-bound blocks (anything referencing `subject.X`) in a
// single `{% for subject in subjects %}` loop, so authors never have to think
// about iteration. Empty sections render a "Nothing found for X" placeholder.

// ---------- inline nodes (paragraph / heading content) ----------

export interface InlineText {
  type: 'text';
  value: string;
}

export interface InlineVariable {
  type: 'var';
  // Dotted path, e.g. "subject.subject_name" or "report.name" or "executive_summary".
  path: string;
  // Filters applied left-to-right. Each renders as `| name(arg, arg)` in Jinja.
  filters: FilterApplication[];
}

export type InlineNode = InlineText | InlineVariable;

export interface FilterApplication {
  name: string;
  // String-ified args. We stringify rather than typing because the supported
  // filters all take primitive args (e.g. date_format('%d %b %Y')).
  args: string[];
}

// ---------- blocks ----------

export interface HeadingBlock {
  id: string;
  kind: 'heading';
  level: 1 | 2 | 3 | 4 | 5 | 6;
  content: InlineNode[];
}

export interface ParagraphBlock {
  id: string;
  kind: 'paragraph';
  content: InlineNode[];
}

export interface DividerBlock {
  id: string;
  kind: 'divider';
}

export interface SectionBlock {
  id: string;
  kind: 'section';
  // ScopeComponent slug. The serialiser wraps this in a per-subject loop
  // automatically and adds a "Nothing found for X" fallback when the section
  // is empty for a subject.
  slug: string;
}

export interface VariableBlock {
  id: string;
  kind: 'variable';
  path: string;
  filters: FilterApplication[];
}

export interface RawBlock {
  id: string;
  kind: 'raw';
  // Verbatim Jinja2. Produced only by the legacy-migration parser when it
  // hits a construct outside the v1 block taxonomy (loops, conditionals,
  // set, include, custom expressions). Authors can't add Raw blocks from
  // the palette; they can delete them or open the Advanced editor.
  source: string;
}

export type Block =
  | HeadingBlock
  | ParagraphBlock
  | DividerBlock
  | SectionBlock
  | VariableBlock
  | RawBlock;

export type BlockKind = Block['kind'];

// ---------- top-level tree ----------

export const TEMPLATE_BLOCKS_VERSION = 1 as const;

export interface BlockTree {
  version: typeof TEMPLATE_BLOCKS_VERSION;
  blocks: Block[];
}

export function emptyBlockTree(): BlockTree {
  return { version: TEMPLATE_BLOCKS_VERSION, blocks: [] };
}

export function isBlockTree(value: unknown): value is BlockTree {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Partial<BlockTree>;
  return v.version === TEMPLATE_BLOCKS_VERSION && Array.isArray(v.blocks);
}

// ---------- runtime validation ----------
//
// `isBlockTree` is a shallow type guard. `validateBlockTree` walks the full
// structure and accumulates path-aware error messages. Used on load to detect
// backend drift — if the saved tree fails validation, the editor falls back
// to Advanced mode rather than crashing on bad data.
//
// Mirror of `.docs/template-blocks-schema-backend-ask.md`. Keep in sync if
// the schema evolves.

export type ValidationResult = { ok: true; tree: BlockTree } | { ok: false; errors: string[] };

export function validateBlockTree(value: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isObject(value)) {
    return { ok: false, errors: ['root: expected object'] };
  }
  if ((value as { version?: unknown }).version !== TEMPLATE_BLOCKS_VERSION) {
    errors.push(`root.version: expected ${TEMPLATE_BLOCKS_VERSION}`);
  }
  const blocks = (value as { blocks?: unknown }).blocks;
  if (!Array.isArray(blocks)) {
    errors.push('root.blocks: expected array');
    return { ok: false, errors };
  }
  blocks.forEach((b, i) => validateBlock(b, `blocks[${i}]`, errors));
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, tree: value as BlockTree };
}

function validateBlock(block: unknown, path: string, errors: string[]) {
  if (!isObject(block)) {
    errors.push(`${path}: expected object`);
    return;
  }
  const id = (block as { id?: unknown }).id;
  if (typeof id !== 'string' || id.length === 0) {
    errors.push(`${path}.id: expected non-empty string`);
  }
  const kind = (block as { kind?: unknown }).kind;
  switch (kind) {
    case 'heading':
      validateHeading(block, path, errors);
      break;
    case 'paragraph':
      validateParagraph(block, path, errors);
      break;
    case 'divider':
      validateDivider(block, path, errors);
      break;
    case 'section':
      validateSection(block, path, errors);
      break;
    case 'variable':
      validateVariable(block, path, errors);
      break;
    case 'raw':
      validateRaw(block, path, errors);
      break;
    default:
      errors.push(
        `${path}.kind: expected one of heading|paragraph|divider|section|variable|raw, got ${JSON.stringify(kind)}`
      );
  }
}

function validateHeading(block: object, path: string, errors: string[]) {
  const level = (block as { level?: unknown }).level;
  if (typeof level !== 'number' || !Number.isInteger(level) || level < 1 || level > 6) {
    errors.push(`${path}.level: expected integer 1..6`);
  }
  validateInlineContent((block as { content?: unknown }).content, `${path}.content`, errors);
}

function validateParagraph(block: object, path: string, errors: string[]) {
  validateInlineContent((block as { content?: unknown }).content, `${path}.content`, errors);
}

function validateDivider(_block: object, _path: string, _errors: string[]) {
  // No fields beyond id/kind.
}

function validateSection(block: object, path: string, errors: string[]) {
  const slug = (block as { slug?: unknown }).slug;
  if (typeof slug !== 'string' || slug.length === 0) {
    errors.push(`${path}.slug: expected non-empty string`);
  }
}

function validateVariable(block: object, path: string, errors: string[]) {
  const p = (block as { path?: unknown }).path;
  if (typeof p !== 'string' || p.length === 0) {
    errors.push(`${path}.path: expected non-empty string`);
  }
  validateFilters((block as { filters?: unknown }).filters, `${path}.filters`, errors);
}

function validateRaw(block: object, path: string, errors: string[]) {
  const source = (block as { source?: unknown }).source;
  if (typeof source !== 'string') {
    errors.push(`${path}.source: expected string`);
  }
}

function validateInlineContent(value: unknown, path: string, errors: string[]) {
  if (!Array.isArray(value)) {
    errors.push(`${path}: expected array`);
    return;
  }
  value.forEach((n, i) => validateInlineNode(n, `${path}[${i}]`, errors));
}

function validateInlineNode(node: unknown, path: string, errors: string[]) {
  if (!isObject(node)) {
    errors.push(`${path}: expected object`);
    return;
  }
  const type = (node as { type?: unknown }).type;
  if (type === 'text') {
    if (typeof (node as { value?: unknown }).value !== 'string') {
      errors.push(`${path}.value: expected string`);
    }
  } else if (type === 'var') {
    const p = (node as { path?: unknown }).path;
    if (typeof p !== 'string' || p.length === 0) {
      errors.push(`${path}.path: expected non-empty string`);
    }
    validateFilters((node as { filters?: unknown }).filters, `${path}.filters`, errors);
  } else {
    errors.push(`${path}.type: expected 'text' or 'var', got ${JSON.stringify(type)}`);
  }
}

function validateFilters(value: unknown, path: string, errors: string[]) {
  if (!Array.isArray(value)) {
    errors.push(`${path}: expected array`);
    return;
  }
  value.forEach((f, i) => {
    const fp = `${path}[${i}]`;
    if (!isObject(f)) {
      errors.push(`${fp}: expected object`);
      return;
    }
    const name = (f as { name?: unknown }).name;
    if (typeof name !== 'string' || name.length === 0) {
      errors.push(`${fp}.name: expected non-empty string`);
    }
    const args = (f as { args?: unknown }).args;
    if (!Array.isArray(args)) {
      errors.push(`${fp}.args: expected array`);
    } else if (!args.every((a) => typeof a === 'string')) {
      errors.push(`${fp}.args: expected array of strings`);
    }
  });
}

function isObject(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}
